import { ImportDuplicateStrategy, ImportEntity, type ImportPreview } from '@paintscout/api'
import { countNoun } from './countNoun'

/**
 * The Review step's states, in order of precedence (#24977, #25036, #25038, #25040):
 * - `error`: the preview failed.
 * - `loading`: no preview has loaded since entering Review. The body is one spinner, so no card
 *   shows and then vanishes (#24977).
 * - `nothingToImport`: no row creates or updates a record (#25038).
 * - `noDuplicates`: nothing in the file matches PaintScout or repeats within the file (#25040).
 * - `duplicates`: the strategy cards, then the counts.
 *
 * After a duplicate-strategy change the preview reloads. The step then keeps the previous
 * preview's layout and only the counts wait (`reloading`). A strategy change is only possible
 * while the Duplicates section shows, and `duplicateCount` is the same under every strategy, so
 * the layout cannot jump.
 */
export type ReviewView = 'error' | 'loading' | 'nothingToImport' | 'noDuplicates' | 'duplicates'

export interface ReviewState {
  view: ReviewView
  /** The preview the layout is drawn from: the loaded one, or the previous one while reloading. */
  layoutPreview?: ImportPreview
  /** True while a strategy change reloads the counts. The counts are unknown until it ends. */
  reloading: boolean
}

export interface ReviewNouns {
  noun: string
  nounPlural: string
}

export function getReviewState(args: {
  preview?: ImportPreview
  previousPreview?: ImportPreview
  loading: boolean
  hasError: boolean
}): ReviewState {
  const { preview, previousPreview, loading, hasError } = args

  if (hasError) {
    return { view: 'error', reloading: false }
  }

  const loaded = loading ? undefined : preview
  if (!loaded) {
    if (previousPreview) {
      return { view: getLoadedView(previousPreview, true), layoutPreview: previousPreview, reloading: true }
    }
    return { view: 'loading', reloading: false }
  }

  return { view: getLoadedView(loaded, false), layoutPreview: loaded, reloading: false }
}

function getLoadedView(preview: ImportPreview, reloading: boolean): ReviewView {
  // While reloading the counts are unknown, so only the strategy-independent layout is kept.
  if (!reloading && preview.willCreate + preview.willUpdate === 0) {
    return 'nothingToImport'
  }
  return preview.duplicateCount === 0 ? 'noDuplicates' : 'duplicates'
}

export function getMatchedBy(entity: ImportEntity): string {
  return entity === ImportEntity.Contact ? 'email' : 'name'
}

/** Bold line under the strategy cards: what the import will do with the selected strategy (#25036). */
export function getReviewHeadline(
  preview: ImportPreview,
  strategy: ImportDuplicateStrategy,
  { noun, nounPlural }: ReviewNouns
): string {
  const parts: string[] = []

  if (preview.willCreate > 0) {
    parts.push(`${countNoun(preview.willCreate, `new ${noun}`, `new ${nounPlural}`)} to import`)
  }

  if (strategy === ImportDuplicateStrategy.Skip) {
    if (preview.willSkip > 0) {
      parts.push(`${countNoun(preview.willSkip, `existing ${noun}`, `existing ${nounPlural}`)} to skip`)
    }
  } else if (preview.willUpdate > 0) {
    parts.push(`${countNoun(preview.willUpdate, `existing ${noun}`, `existing ${nounPlural}`)} to update`)
  }

  return parts.join(' · ')
}

/**
 * The job re-reads each matched record and reports an update that changes nothing as skipped. The
 * preview cannot know which ones, so the step says so whenever it counts updates (#25036).
 */
export function getUpdateSkipNote(
  preview: ImportPreview,
  strategy: ImportDuplicateStrategy,
  { nounPlural }: ReviewNouns
): string | undefined {
  if (preview.willUpdate === 0) {
    return undefined
  }
  if (strategy === ImportDuplicateStrategy.FillEmpty) {
    return `Existing ${nounPlural} with nothing new to add are skipped.`
  }
  if (strategy === ImportDuplicateStrategy.Overwrite) {
    return `Existing ${nounPlural} with nothing to change are skipped.`
  }
  return undefined
}

/** The Review footer's primary button. Without a loaded preview the count is unknown. */
export function getImportButtonLabel(preview: ImportPreview | undefined, { noun, nounPlural }: ReviewNouns): string {
  if (!preview) {
    return `Import ${nounPlural}`
  }

  const { willCreate, willUpdate } = preview
  if (willCreate > 0 && willUpdate > 0) {
    return `Import ${willCreate}, update ${willUpdate}`
  }
  if (willUpdate > 0) {
    return `Update ${countNoun(willUpdate, noun, nounPlural)}`
  }
  if (willCreate > 0) {
    return `Import ${countNoun(willCreate, noun, nounPlural)}`
  }
  return 'Done'
}

/** The line under "No duplicates found" (#25040). */
export function getNoDuplicatesSubtitle(
  preview: ImportPreview,
  entity: ImportEntity,
  { noun, nounPlural }: ReviewNouns
) {
  const matchedBy = getMatchedBy(entity)
  const count = preview.willCreate

  // With error rows, "All" would count rows that are not imported.
  if (preview.errors.length > 0) {
    return `${countNoun(count, noun, nounPlural)} ${count === 1 ? 'is' : 'are'} new to PaintScout, matched by ${matchedBy}.`
  }
  if (count === 1) {
    return `The ${noun} is new to PaintScout, matched by ${matchedBy}.`
  }
  return `All ${count} ${nounPlural} are new to PaintScout, matched by ${matchedBy}.`
}

/** Why there is nothing to import (#25038). */
export function getNothingToImportReason(preview: ImportPreview, { noun, nounPlural }: ReviewNouns): string {
  const skipped = preview.willSkip
  const errorRows = preview.errors.length

  if (skipped > 0 && errorRows > 0) {
    return (
      `${countNoun(skipped, noun, nounPlural)} in your file ${skipped === 1 ? 'is' : 'are'} already in PaintScout ` +
      `and ${countNoun(errorRows, 'row has', 'rows have')} errors. ` +
      `Skip duplicates leaves the existing ${nounPlural} as they are.`
    )
  }
  if (skipped === 1) {
    return `The ${noun} in your file is already in PaintScout. Skip duplicates leaves it as it is.`
  }
  if (skipped > 0) {
    return `All ${skipped} ${nounPlural} in your file are already in PaintScout. Skip duplicates leaves them as they are.`
  }
  if (errorRows > 0) {
    return 'Every row in your file has an error. Fix the rows in the downloaded file and import it again.'
  }
  return 'Your file has no rows to import.'
}
